/**
 * Web chat queue edit prototype.
 * Access: /?test=chat-queue-edit
 *
 * Bet: docs/product/bets/2026-10-23-chat-queue-send-now.md
 *
 * A pencil (or the row text) loads that message into the composer under an
 * "Editing queued message" bar. Cancel and Escape leave the edit and put the
 * previous composer draft back. The row stays in the queue, highlighted.
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ContentEditableChatInput } from '@/components/ui/contenteditable-chat-input'
import { ChatModeSelector, type ChatMode } from '@/components/layout/chat-mode-selector'
import { ChatPaneActiveDocBadge } from '@/components/layout/chat-pane-active-doc-badge'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { Brain, ChevronDown, ChevronRight, Pencil, Square, X } from 'lucide-react'

type QueueItem = {
  id: string
  text: string
  imageLabel?: string
}

type EditSession = {
  id: string
  original: string
  stashedComposer: string
}

const SEED: QueueItem[] = [
  { id: 'due-date', text: 'Check the due date first' },
  { id: 'basecamp', text: 'Also draft the Basecamp note', imageLabel: 'brief.png' },
]

const ids = testIds.chatQueueEditPrototype

export function ChatQueueEditPrototype() {
  const [queue, setQueue] = useState<QueueItem[]>(SEED)
  const [expanded, setExpanded] = useState(true)
  const [streaming, setStreaming] = useState(true)
  const [editing, setEditing] = useState<EditSession | null>(null)
  const [composer, setComposer] = useState('')
  const [log, setLog] = useState('Reply is streaming. Hover a row for the pencil.')
  const [seq, setSeq] = useState(1)
  const [chatMode, setChatMode] = useState<ChatMode>('agent')
  const [showContextChip, setShowContextChip] = useState(true)
  const editingId = editing?.id

  useEffect(() => {
    if (!editingId) return
    const el = document.querySelector(`[data-testid="${ids.composer}"]`)
    if (el instanceof HTMLElement) el.focus()
  }, [editingId])

  const holdingOldest = Boolean(editing && queue[0]?.id === editing.id)
  const note = (message: string) => setLog(message)

  const beginEdit = (item: QueueItem) => {
    if (editing?.id === item.id) {
      const el = document.querySelector(`[data-testid="${ids.composer}"]`)
      if (el instanceof HTMLElement) el.focus()
      return
    }
    const stash = editing ? editing.stashedComposer : composer
    if (editing) {
      const next = composer.trim()
      if (next) {
        const currentId = editing.id
        setQueue((prev) => prev.map((row) => (
          row.id === currentId ? { ...row, text: next } : row
        )))
      }
    }
    setEditing({ id: item.id, original: item.text, stashedComposer: stash })
    setComposer(item.text)
    setExpanded(true)
    note('Editing queued message. Cancel or Escape leaves it.')
  }

  const leaveEdit = (mode: 'save' | 'cancel') => {
    if (!editing) return
    if (mode === 'save') {
      const next = composer.trim()
      if (!next) {
        note('Empty text is not saved. Cancel to leave the edit.')
        return
      }
      const currentId = editing.id
      setQueue((prev) => prev.map((row) => (
        row.id === currentId ? { ...row, text: next } : row
      )))
      note(editing.stashedComposer.trim()
        ? 'Saved. Your follow-up draft is back in the composer.'
        : 'Saved. The row stays queued.')
    } else {
      note('Left the edit. The queued text is unchanged.')
    }
    setComposer(editing.stashedComposer)
    setEditing(null)
  }

  const removeAt = (id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id))
    if (editing?.id === id) {
      setComposer(editing.stashedComposer)
      setEditing(null)
    }
    note('Cancelled queued message.')
  }

  const sendNow = (item: QueueItem) => {
    const fromEdit = editing?.id === item.id
    const text = (fromEdit ? composer : item.text).trim()
    if (!text) {
      note('Nothing to send. The message is empty.')
      return
    }
    const stash = fromEdit ? editing.stashedComposer : null
    setQueue((prev) => prev.filter((row) => row.id !== item.id))
    if (fromEdit && editing) {
      setComposer(stash ?? '')
      setEditing(null)
    }
    setStreaming(true)
    note(`Send now stopped the reply and sent: "${text}"`)
  }

  const onStreamEnded = () => {
    const oldest = queue[0]
    if (!oldest) {
      setStreaming(false)
      note('Stream ended. Nothing queued.')
      return
    }
    if (editing?.id === oldest.id) {
      setStreaming(false)
      note('Stream ended. The oldest row is open for edit, so it was held.')
      return
    }
    setQueue((prev) => prev.slice(1))
    setStreaming(true)
    note(`Stream ended. Auto-sent the oldest row: "${oldest.text}"`)
  }

  const onStop = () => {
    setStreaming(false)
    note(queue.length > 0
      ? 'Stopped. Nothing sent. The queue is still here.'
      : 'Stopped. Nothing queued.')
  }

  const queueComposer = () => {
    if (editing) {
      leaveEdit('save')
      return
    }
    const text = composer.trim()
    if (!text) return
    const id = `draft-${seq}`
    setSeq((n) => n + 1)
    setComposer('')
    setExpanded(true)
    if (streaming) {
      setQueue((prev) => [...prev, { id, text }])
      note('Queued from the composer.')
      return
    }
    setStreaming(true)
    note(`Sent from the composer: "${text}"`)
  }

  const reset = () => {
    setQueue(SEED)
    setEditing(null)
    setComposer('')
    setStreaming(true)
    setExpanded(true)
    setSeq(1)
    note('Reset. Reply is streaming again.')
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground p-6"
      data-testid={ids.root}
    >
      <header className="max-w-xl mx-auto mb-6 space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Prototype</p>
        <h1 className="text-2xl font-semibold">Chat queue edit</h1>
        <p className="text-sm text-muted-foreground">
          Hover a row for the pencil. It loads that message into the composer.
          A bar names the mode. Cancel or Escape leaves it and puts your
          follow-up draft back. The row stays highlighted until you leave.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Badge variant="outline">pencil on hover</Badge>
          <Badge variant="outline">Editing queued message</Badge>
          <Badge variant="secondary">not wired into chat yet</Badge>
        </div>
        <a href="/?test=index" className="text-xs text-primary hover:underline inline-block">
          All prototypes
        </a>
      </header>

      <div className="max-w-xl mx-auto space-y-4">
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-3 py-3 border-b border-border space-y-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Chat</p>
            <p className="text-sm">Looking up the due date on the brief...</p>
            {streaming ? (
              <p className="text-xs text-muted-foreground italic">Reading documents</p>
            ) : (
              <p className="text-xs text-muted-foreground">Reply idle</p>
            )}
          </div>

          <div data-testid={ids.strip}>
            {queue.length > 0 ? (
              <div className="border-t border-border/50 bg-muted/20">
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {expanded
                    ? <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                  <span className="font-medium">{queue.length} Queued</span>
                  {holdingOldest ? (
                    <span className="ml-auto text-primary">Oldest row held while editing</span>
                  ) : streaming ? (
                    <span className="ml-auto text-muted-foreground/60">
                      Sends after this reply finishes
                    </span>
                  ) : null}
                </button>
                {expanded ? (
                  <div className="divide-y divide-border/40 pb-1">
                    {queue.map((item) => {
                      const open = editing?.id === item.id
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            'group flex items-center gap-2 px-3 py-1.5',
                            open && 'mx-2 my-1 rounded-md bg-primary/15 ring-1 ring-primary/50',
                          )}
                          data-testid={ids.item(item.id)}
                        >
                          {item.imageLabel ? (
                            <div className="h-8 min-w-8 px-1 rounded border border-border bg-muted text-[10px] text-muted-foreground flex items-center justify-center shrink-0">
                              {item.imageLabel}
                            </div>
                          ) : null}
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left"
                            onClick={() => beginEdit(item)}
                          >
                            <p className="text-xs truncate text-foreground/80">{item.text}</p>
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                              {streaming ? 'Sends after message finishes' : 'Queued'}
                            </p>
                          </button>
                          <button
                            type="button"
                            onClick={() => beginEdit(item)}
                            className={cn(
                              'inline-flex items-center justify-center rounded shrink-0 overflow-hidden',
                              'w-0 p-0 opacity-0 pointer-events-none',
                              'group-hover:w-7 group-hover:opacity-100 group-hover:pointer-events-auto',
                              'focus-visible:w-7 focus-visible:p-1 focus-visible:opacity-100 focus-visible:pointer-events-auto',
                              'transition-[width,opacity] duration-150',
                              open
                                ? 'text-primary group-hover:bg-primary/20'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                            )}
                            aria-label="Edit queued message"
                            data-testid={ids.edit(item.id)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => sendNow(item)}
                            className="text-xs px-2 py-0.5 rounded text-primary hover:bg-primary/10 transition-colors shrink-0"
                            data-testid={ids.sendNow(item.id)}
                          >
                            Send now
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAt(item.id)}
                            className="p-0.5 hover:bg-muted rounded shrink-0"
                            aria-label="Cancel queued message"
                            data-testid={ids.cancel(item.id)}
                          >
                            <X className="w-3 h-3 text-muted-foreground" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground border-t border-border/50">
                Queue empty
              </p>
            )}

            {editing ? (
              <div
                className="flex items-center justify-between gap-3 px-3 py-2 bg-primary/15 border-y border-primary/30"
                data-testid={ids.banner}
              >
                <span className="text-sm font-medium">Editing queued message</span>
                <button
                  type="button"
                  onClick={() => leaveEdit('cancel')}
                  className="text-sm font-medium text-primary hover:underline shrink-0"
                  data-testid={ids.exit}
                >
                  Cancel
                </button>
              </div>
            ) : null}

            <div className="border-t border-border p-3">
              {editing?.stashedComposer.trim() ? (
                <p className="text-[11px] text-muted-foreground mb-1.5">
                  Your follow-up draft comes back when you leave this edit.
                </p>
              ) : null}
              <ContentEditableChatInput
                value={composer}
                references={[]}
                onReferencesChange={() => {}}
                onChange={setComposer}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && editing) {
                    e.preventDefault()
                    leaveEdit('cancel')
                    return
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    queueComposer()
                  }
                }}
                onSend={queueComposer}
                onStop={streaming && !editing ? onStop : undefined}
                placeholder={
                  streaming && !editing
                    ? 'Queue next message...'
                    : 'Type @ to mention, or drag files here...'
                }
                showToolbar
                aria-label={editing ? 'Editing queued message' : 'Chat input'}
                data-testid={ids.composer}
                modeSelector={<ChatModeSelector value={chatMode} onChange={setChatMode} />}
                modelBadge={(
                  <span className="inline-flex h-8 min-w-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground shadow-xs">
                    <Brain className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                    <span className="truncate">Grok 4.7</span>
                  </span>
                )}
                composerContextRow={showContextChip ? (
                  <ChatPaneActiveDocBadge
                    variant="inline"
                    activeDocumentId="proto-symposium"
                    activeDocumentTitle="2026-10-24 Symposium recap - Dan Nelson (full)"
                    activeDocDismissed={false}
                    onDismiss={() => setShowContextChip(false)}
                    references={[]}
                  />
                ) : null}
              />
            </div>
          </div>
        </div>

        <p className="text-sm" data-testid={ids.log}>{log}</p>

        <div className="flex flex-wrap gap-2">
          {streaming ? (
            <Button size="sm" variant="secondary" onClick={onStop} data-testid={ids.stop}>
              <Square className="w-3 h-3 fill-current" />
              Stop
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setStreaming(true)
                note('Simulating a reply...')
              }}
            >
              Simulate reply
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={onStreamEnded} data-testid={ids.streamEnded}>
            Stream ended
          </Button>
          <Button size="sm" variant="outline" onClick={reset}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  )
}
